import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { Input, Textarea } from "../ui";
import { useChat, SearchableUser } from "../../context/ChatContext";
import { useTheme } from "../../context/ThemeContext";
import {
  getPrimaryText,
  getMutedText,
  getInputStyles,
  getLightBorder,
} from "../../utils/themeUtils";
import { ChatConversation, UserInfo, SupportCategory } from "../../api/chatAPI";
import { isMemberRole as roleIsMember } from "../../utils/roles";
import { submitSupportTicket } from "../../api/supportTicketAPI";
import { AuthContext } from "../../context/AuthContext";
import { getAuthSource, getTenantId } from "../../api/apiHelpers";
import { sendAiChatMessage } from "../../api/aiAPI";
import { captureError, captureApiError } from "../../utils/sentry";
import StaffAIAssistantPanel from "./StaffAIAssistantPanel";
import Tooltip from "./Tooltip";

// ============================================
// ICONS (inline SVG for minimal dependencies)
// ============================================

const MessageIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const MinimizeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
  </svg>
);

const BackIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-10 h-10 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const HeadsetIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 18v-6a9 9 0 0118 0v6" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const ArchiveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
  </svg>
);

const UnarchiveIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4l3-3m0 0l3 3m-3-3v6" />
  </svg>
);

const ChatIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ============================================
// CHAT WIDGET COMPONENT
// ============================================

type ChatView = "home" | "list" | "conversation" | "newMessage" | "newChannel" | "supportTicketForm" | "ticketSuccess" | "categorySelector" | "aiConversation" | "staffAi";

const ChatWidget: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useContext(AuthContext);
  const {
    conversations,
    archivedConversations,
    activeConversation,
    messages,
    isLoading,
    isWidgetOpen,
    isWidgetMinimized,
    unreadCount,
    typingUsers,
    supportAvailable,
    isWaitingForAgent,
    showInChatTicketForm,
    showCategorySelector,
    assistantPrompt,
    assistantPromptNonce,
    clearAssistantPrompt,
    openWidget,
    closeWidget,
    submitInChatTicket,
    minimizeWidget,
    maximizeWidget,
    selectConversation,
    sendMessage,
    setTyping,
    openSupportCategorySelector,
    selectSupportCategory,
    cancelSupportCategorySelection,
    searchUsers,
    startDMWithUser,
    getCurrentUserInfo,
    refreshConversations,
    archiveConversation,
    unarchiveConversation,
    fetchArchivedConversations,
  } = useChat();

  // Initial view: staff lands on the card-grid home; members land on
  // their conversation list (their flow stays unchanged).
  const initialView: ChatView =
    roleIsMember(localStorage.getItem("userRole")) ? "list" : "home";
  const [view, setView] = useState<ChatView>(initialView);
  const [messageInput, setMessageInput] = useState("");
  const [, setSettings] = useState<{ primary_color?: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchableUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [channelName, setChannelName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<SearchableUser[]>([]);
  const [isCreatingChannel, setIsCreatingChannel] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketCategory, setTicketCategory] = useState("general");
  const [ticketMessage, setTicketMessage] = useState("");
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  // In-chat ticket form state
  const [inChatTicketSubject, setInChatTicketSubject] = useState("");
  const [inChatTicketCategory, setInChatTicketCategory] = useState("technical");
  const [inChatTicketMessage, setInChatTicketMessage] = useState("");
  const [ticketSubmitError, setTicketSubmitError] = useState<string | null>(null);
  const [isSubmittingInChatTicket, setIsSubmittingInChatTicket] = useState(false);
  // AI chat state
  const [aiSessionId, setAiSessionId] = useState<string | null>(null);
  const [aiMessages, setAiMessages] = useState<Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
  }>>([]);
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [aiCategory, setAiCategory] = useState<SupportCategory | null>(null);
  // Staff AI assistant — increment to reset the embedded panel.
  const [staffAiResetCounter, setStaffAiResetCounter] = useState(0);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const channelNameInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const aiMessagesEndRef = useRef<HTMLDivElement>(null);

  // Check if user can create channels
  // Platform admin: central auth source
  // Tenant owner: tenant_owner auth source
  // Manager: tenant auth + manager role
  const canCreateChannel = useCallback(() => {
    const authSource = getAuthSource();
    const userRole = localStorage.getItem("userRole")?.toLowerCase() || "";

    if (authSource === "central" || authSource === "tenant_owner") {
      // Platform admin or Tenant owner - both can create channels
      return true;
    } else {
      // Tenant auth - only managers can create channels
      return userRole === "manager";
    }
  }, []);

  // Check if user is a member (as opposed to staff/admin)
  const isMemberRole = useMemo(
    () => roleIsMember(localStorage.getItem("userRole")),
    []
  );

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowNewMenu(false);
      }
    };

    if (showNewMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNewMenu]);

  // Load settings from localStorage for tenant branding
  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (e) {
        captureError(e, { component: "ChatWidget", operation: "parseSettings" }, "warning");
      }
    }
  }, []);

  // Brand colors used below come from the --color-primary /
  // --color-primary-hover / --color-primary-text CSS vars set by
  // applyCSSVariables — see CLAUDE.md "Tenant theming" section.

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Scroll to bottom when AI messages change
  useEffect(() => {
    if (aiMessagesEndRef.current) {
      aiMessagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [aiMessages]);

  // Switch view based on active conversation
  useEffect(() => {
    if (activeConversation) {
      setView("conversation");
    } else {
      setView("list");
    }
  }, [activeConversation]);

  // Switch to category selector view when showCategorySelector becomes true
  useEffect(() => {
    if (showCategorySelector) {
      setView("categorySelector");
    }
  }, [showCategorySelector]);

  // When something hands the assistant a prompt (e.g. the global search bar's
  // "Not finding…" row), jump to the AI assistant view. The panel itself
  // reads `assistantPrompt`/`assistantPromptNonce` to start a fresh chat with
  // the text prefilled. Guard on the nonce so we only react to genuinely new
  // asks (not on every re-render).
  const lastAssistantNonceRef = useRef(0);
  useEffect(() => {
    if (!assistantPromptNonce || !assistantPrompt) return;
    if (lastAssistantNonceRef.current === assistantPromptNonce) return;
    lastAssistantNonceRef.current = assistantPromptNonce;
    setView("staffAi");
  }, [assistantPromptNonce, assistantPrompt]);

  // Focus input when conversation opens
  useEffect(() => {
    if (view === "conversation" && inputRef.current) {
      inputRef.current.focus();
    }
  }, [view]);

  // Fetch archived conversations when toggling to archived view
  useEffect(() => {
    if (showArchived) {
      fetchArchivedConversations();
    }
  }, [showArchived, fetchArchivedConversations]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    await sendMessage(messageInput);
    setMessageInput("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);
    setTyping(e.target.value.length > 0);
  };

  const handleBackToList = () => {
    selectConversation(null);
    setView("list");
  };

  const handleConversationClick = (conv: ChatConversation) => {
    selectConversation(conv);
  };

  const handleStartSupportChat = () => {
    openSupportCategorySelector();
  };

  const getAiGreeting = (category: SupportCategory): string => {
    const greetings: Record<string, string> = {
      billing: "Hi! I can help with billing questions — account balance, invoices, payments, and more. What would you like to know?",
      technical: "Hello! I'm here to help with technical issues. Can you describe what's going on?",
      account: "Hi there! I can help with your account, athlete info, and schedules. What do you need?",
      feature: "Hey! I'd love to hear your idea. What feature would you like to see?",
      bug: "Hi! Sorry you're having trouble. Can you describe what happened and what you expected?",
      other: "Hello! How can I help you today?",
    };
    return greetings[category] || greetings.other;
  };

  const getMemberContext = () => {
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const email = localStorage.getItem("email") || "";
    const membershipId = localStorage.getItem("membershipId") || "";
    // Capture where the user is in the app so the LLM can disambiguate
    // intent ("cancel" means cancel-membership on /manage-profile but
    // cancel-event-registration on /events). page_path strips the
    // tenant slug + query/hash so the LLM sees a stable identifier;
    // page_url is the full thing for cases where query params matter
    // (e.g. /manage/store/orders?status=refunded).
    const pathname =
      typeof window !== "undefined" ? window.location.pathname || "" : "";
    const segments = pathname.split("/").filter(Boolean);
    // Drop tenant slug (first segment) for the canonical path so the
    // LLM doesn't have to mentally parse it on every call.
    const pagePath = segments.length > 1 ? `/${segments.slice(1).join("/")}` : "/";
    const pageUrl =
      typeof window !== "undefined" ? window.location.href || "" : "";
    const pageTitle =
      typeof document !== "undefined" ? document.title || "" : "";
    return {
      name: `${firstName} ${lastName}`.trim(),
      email,
      membership_id: membershipId,
      page_path: pagePath,
      page_url: pageUrl,
      page_title: pageTitle,
    };
  };

  const handleCategorySelect = async (category: SupportCategory) => {
    if (isMemberRole) {
      const sessionId = crypto.randomUUID();
      setAiSessionId(sessionId);
      setAiCategory(category);
      setAiMessages([{
        role: 'assistant',
        content: getAiGreeting(category),
        timestamp: new Date().toISOString(),
      }]);
      cancelSupportCategorySelection();
      setView("aiConversation");
    } else {
      await selectSupportCategory(category);
    }
  };

  const handleSendAiMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim() || !aiSessionId || isAiResponding) return;

    const userMsg = messageInput.trim();
    setAiMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setMessageInput("");
    setIsAiResponding(true);

    try {
      const result = await sendAiChatMessage(userMsg, aiSessionId, aiCategory || undefined, getMemberContext());
      if (result?.success && result.data) {
        const responseText = result.data.response.replace("[ESCALATE_TO_HUMAN]", "").trim();
        setAiMessages(prev => [...prev, { role: 'assistant', content: responseText, timestamp: new Date().toISOString() }]);

        if (result.data.should_escalate) {
          setTimeout(() => handleEscalateToHuman(), 1500);
        }
      } else {
        setAiMessages(prev => [...prev, {
          role: 'system',
          content: "Sorry, I'm having trouble responding. Try again or tap the headset icon to talk to a person.",
          timestamp: new Date().toISOString(),
        }]);
      }
    } catch {
      setAiMessages(prev => [...prev, {
        role: 'system',
        content: "Connection issue. Please try again.",
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsAiResponding(false);
    }
  };

  const handleEscalateToHuman = async () => {
    if (!aiCategory) return;

    setAiMessages(prev => [...prev, {
      role: 'system',
      content: "Connecting you with a team member...",
      timestamp: new Date().toISOString(),
    }]);

    // Build transcript from AI conversation
    const transcript = aiMessages
      .filter(m => m.role !== 'system')
      .map(m => `${m.role === 'user' ? 'Member' : 'AI'}: ${m.content}`)
      .join('\n\n');

    // Use existing human support flow
    await selectSupportCategory(aiCategory);

    // Send AI transcript as context message (after conversation is created)
    if (transcript) {
      await sendMessage(`[AI conversation transcript]\n\n${transcript}\n\n[Member requested human assistance]`);
    }

    // Clear AI state
    setAiSessionId(null);
    setAiMessages([]);
    setAiCategory(null);
  };

  const handleBackFromAiChat = () => {
    setAiSessionId(null);
    setAiMessages([]);
    setAiCategory(null);
    setView("list");
  };

  const handleCancelCategorySelection = () => {
    cancelSupportCategorySelection();
    setView("list");
  };

  const handleToggleMenu = () => {
    setShowNewMenu(!showNewMenu);
  };

  const handleNewMessage = () => {
    setShowNewMenu(false);
    setView("newMessage");
    setSearchQuery("");
    setSearchResults([]);
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const handleNewChannel = () => {
    setShowNewMenu(false);
    setView("newChannel");
    setChannelName("");
    setSelectedUsers([]);
    setSearchQuery("");
    setSearchResults([]);
    setTimeout(() => channelNameInputRef.current?.focus(), 100);
  };

  const handleAddUserToChannel = (user: SearchableUser) => {
    // Don't add if already selected
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleRemoveUserFromChannel = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const handleCreateChannel = async () => {
    if (!channelName.trim() || selectedUsers.length === 0) return;

    setIsCreatingChannel(true);
    try {
      const currentUserInfo = getCurrentUserInfo();
      if (!currentUserInfo) {
        captureError(new Error("No current user info available"), { component: "ChatWidget", operation: "createChannel" });
        return;
      }

      // Create channel via API
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/chat/channels`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": localStorage.getItem("tenantId") || "",
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: JSON.stringify({
          name: channelName.trim(),
          createdBy: currentUserInfo,
          participants: selectedUsers.map(u => ({
            id: u.id,
            type: u.role || "member",
            display_name: `${u.first_name} ${u.last_name}`.trim(),
          })),
        }),
      });

      const result = await response.json();
      if (result.success && result.data) {
        // Refresh conversations list to include the new channel
        await refreshConversations();
        // Select the new channel and go to conversation view
        selectConversation(result.data);
        setView("conversation");
        // Reset channel creation state
        setChannelName("");
        setSelectedUsers([]);
      } else {
        captureApiError(new Error(result.error), "createChannel", { component: "ChatWidget" });
      }
    } catch (error) {
      captureApiError(error, "createChannel", { component: "ChatWidget" });
    } finally {
      setIsCreatingChannel(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.length >= 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        const results = await searchUsers(query);
        setSearchResults(results);
        setIsSearching(false);
      }, 300);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  };

  const handleSelectUser = async (selectedUser: SearchableUser) => {
    const userInfo: UserInfo = {
      id: selectedUser.id,
      type: "tenant_user",
      tenant_id: null, // Will be filled by the context
      display_name: `${selectedUser.first_name} ${selectedUser.last_name}`.trim(),
    };

    await startDMWithUser(userInfo);
    setSearchQuery("");
    setSearchResults([]);
  };


  const handleArchiveConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    await archiveConversation(conversationId);
  };

  const handleUnarchiveConversation = async (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    await unarchiveConversation(conversationId);
  };

  const handleSubmitTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) return;

    const currentUser = getCurrentUserInfo();
    if (!currentUser) return;

    setIsSubmittingTicket(true);
    try {
      const tenantId = getTenantId();
      const authSource = getAuthSource();
      const token = localStorage.getItem("token");

      // Get tenant name from settings
      const settingsStr = localStorage.getItem("settings");
      let tenantName = "";
      if (settingsStr) {
        try {
          const settingsData = JSON.parse(settingsStr);
          tenantName = settingsData.name || settingsData.club_name || "";
        } catch (e) {
          captureError(e, { component: "ChatWidget", operation: "parseSettings" }, "warning");
        }
      }

      const result = await submitSupportTicket(token, {
        name: currentUser.display_name,
        email: user?.email || "",
        subject: ticketSubject.trim(),
        category: ticketCategory,
        priority: "medium",
        description: ticketMessage.trim(),
        role: localStorage.getItem("userRole") || "member",
        tenantId: tenantId || "",
        tenantName: tenantName,
        userId: currentUser.id,
        authSource: authSource || "tenant",
      });

      if (result.success && result.data) {
        setView("ticketSuccess");
        // Reset form
        setTicketSubject("");
        setTicketCategory("general");
        setTicketMessage("");
      } else {
        captureApiError(new Error(result.message), "submitTicket", { component: "ChatWidget" });
      }
    } catch (error) {
      captureApiError(error, "submitTicket", { component: "ChatWidget" });
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  const handleGoToTickets = () => {
    // Navigate to the support page - for now just close the widget
    // The user can find their tickets on the Support page
    closeWidget();
    setView("list");
  };

  const handleSubmitInChatTicket = async () => {
    if (!inChatTicketSubject.trim() || !inChatTicketMessage.trim()) return;

    setIsSubmittingInChatTicket(true);
    setTicketSubmitError(null);
    try {
      const success = await submitInChatTicket(
        inChatTicketSubject.trim(),
        inChatTicketCategory,
        inChatTicketMessage.trim()
      );

      if (success) {
        // Reset form
        setInChatTicketSubject("");
        setInChatTicketCategory("technical");
        setInChatTicketMessage("");
      } else {
        setTicketSubmitError("Failed to submit ticket. Please try again.");
      }
    } catch (error) {
      captureApiError(error, "submitInChatTicket", { component: "ChatWidget" });
      setTicketSubmitError("An error occurred. Please try again.");
    } finally {
      setIsSubmittingInChatTicket(false);
    }
  };

  const getConversationDisplayName = (conv: ChatConversation): string => {
    if (conv.name) return conv.name;
    if (conv.type === "support") return "Support Chat";

    // For DMs, find the other participant
    // Note: Use getCurrentUserInfo() instead of user?.id because tenant_owner
    // auth stores user info in localStorage, not in the Supabase session
    const currentUserId = getCurrentUserInfo()?.id;
    const otherParticipant = conv.participants?.find(
      (p) => p.user_id !== currentUserId
    );
    return otherParticipant?.display_name || "Unknown User";
  };

  const getConversationAvatar = (conv: ChatConversation): React.ReactNode => {
    if (conv.type === "support") {
      return (
        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
          <HeadsetIcon />
        </div>
      );
    }

    const initials = getConversationDisplayName(conv)
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

    return (
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
      >
        {initials || "?"}
      </div>
    );
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  // Minimized bubble
  if (!isWidgetOpen || isWidgetMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={isWidgetMinimized ? maximizeWidget : openWidget}
          className="relative w-14 h-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
        >
          <MessageIcon />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Full widget
  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={`w-80 sm:w-96 h-[500px] rounded-xl shadow-2xl flex flex-col overflow-hidden border ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode
              ? "bg-gray-900 border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            {((view === "list" && !isMemberRole) || view === "conversation" || view === "newMessage" || view === "newChannel" || view === "supportTicketForm" || view === "ticketSuccess" || view === "categorySelector" || view === "aiConversation" || view === "staffAi") && (
              <button
                aria-label="Back"
                onClick={() => {
                  // Staff: return to the card-grid home; member: stay on list.
                  const rootView: ChatView = isMemberRole ? "list" : "home";
                  if (view === "aiConversation") {
                    handleBackFromAiChat();
                  } else if (view === "categorySelector") {
                    handleCancelCategorySelection();
                  } else if (view === "list") {
                    setView(rootView);
                  } else if (view === "newMessage" || view === "newChannel" || view === "supportTicketForm" || view === "ticketSuccess" || view === "staffAi") {
                    setView(rootView);
                    setSearchQuery("");
                    setSearchResults([]);
                    setChannelName("");
                    setSelectedUsers([]);
                  } else {
                    // conversation view falls through to handleBackToList,
                    // which clears the active conversation and lands on
                    // the conversation list (one level up from a chat).
                    handleBackToList();
                  }
                }}
                className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${getMutedText(isDarkMode)}`}
              >
                <BackIcon />
              </button>
            )}
            <h3 className={`font-semibold ${getPrimaryText(isDarkMode)}`}>
              {view === "conversation" && activeConversation
                ? getConversationDisplayName(activeConversation!)
                : view === "newMessage"
                  ? "New Message"
                  : view === "newChannel"
                    ? "New Channel"
                    : view === "supportTicketForm"
                      ? "Submit Support Ticket"
                      : view === "ticketSuccess"
                        ? "Ticket Submitted"
                        : view === "categorySelector"
                          ? "Select Category"
                          : view === "aiConversation"
                            ? "AI Assistant"
                            : view === "staffAi"
                              ? "Staff Assistant"
                              : "Messages"}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {view === "list" && (
              isMemberRole ? (
                <Tooltip content="New"><button
                  onClick={() => openSupportCategorySelector()}
                  className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1 text-sm font-medium ${getMutedText(isDarkMode)}`}

                >
                  <PlusIcon />
                  <span>New</span>
                </button></Tooltip>
              ) : (
                <div className="relative" ref={menuRef}>
                  <Tooltip content="New"><button
                    onClick={handleToggleMenu}
                    className={`px-2 py-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center gap-1 text-sm font-medium ${getMutedText(isDarkMode)}`}

                  >
                    <PlusIcon />
                    <span>New</span>
                  </button></Tooltip>
                  {/* Dropdown Menu */}
                  {showNewMenu && (
                    <div
                      className={`absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg border z-50 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <button
                        onClick={handleNewMessage}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left rounded-t-lg transition-colors ${
                          isDarkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        <ChatIcon />
                        <span>New Message</span>
                      </button>
                      {canCreateChannel() && (
                        <button
                          onClick={handleNewChannel}
                          className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                            isDarkMode
                              ? "hover:bg-gray-700 text-gray-200"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                        >
                          <UsersIcon />
                          <span>New Channel</span>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowNewMenu(false);
                          openSupportCategorySelector();
                        }}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors ${
                          isDarkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        <HeadsetIcon />
                        <div className="flex flex-col">
                          <span>Contact Support</span>
                          {supportAvailable ? (
                            <span className="text-xs text-green-500">Agents online</span>
                          ) : (
                            <span className="text-xs text-amber-500">Leave a message</span>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={() => {
                          setShowNewMenu(false);
                          setView("staffAi");
                        }}
                        className={`w-full px-4 py-2.5 flex items-center gap-3 text-left rounded-b-lg transition-colors ${
                          isDarkMode
                            ? "hover:bg-gray-700 text-gray-200"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        <span aria-hidden className="text-purple-500">✨</span>
                        <div className="flex flex-col">
                          <span>Ask AI</span>
                          <span className={`text-xs ${getMutedText(isDarkMode)}`}>
                            Look up members, find policies, etc.
                          </span>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              )
            )}
            {view === "staffAi" && (
              <Tooltip content="Start a new AI conversation"><button
                onClick={() => setStaffAiResetCounter((n) => n + 1)}
                className={`text-xs px-2 py-1 rounded hover:underline ${getMutedText(isDarkMode)}`}

              >
                New chat
              </button></Tooltip>
            )}
            <button aria-label="Minimize chat"
              onClick={minimizeWidget}
              className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${getMutedText(isDarkMode)}`}
            >
              <MinimizeIcon />
            </button>
            <button aria-label="Close chat"
              onClick={closeWidget}
              className={`p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500`}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {view === "home" ? (
            // Card-grid home (staff). Six entry points; clicking a card
            // sets the right state and transitions to the matching view.
            // Members never see this view — they default to "list".
            <div className="p-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setView("staffAi")}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700/50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-purple-600 dark:text-purple-300">
                  ✨
                </div>
                <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>Ask AI</p>
                <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Look up members, find policies
                </p>
              </button>

              <button
                onClick={() => {
                  setShowArchived(false);
                  setView("list");
                }}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700/50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-300">
                  <ChatIcon />
                </div>
                <div className="flex items-center gap-2">
                  <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>
                    Active Chats
                  </p>
                  {unreadCount > 0 && (
                    <span className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 min-w-[18px] text-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Conversations &amp; channels
                </p>
              </button>

              <button
                onClick={handleNewMessage}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700/50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-300">
                  <ChatIcon />
                </div>
                <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>New Message</p>
                <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Direct message a teammate
                </p>
              </button>

              {canCreateChannel() && (
                <button
                  onClick={handleNewChannel}
                  className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                    isDarkMode
                      ? "border-gray-700 hover:bg-gray-700/50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-300">
                    <UsersIcon />
                  </div>
                  <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>New Channel</p>
                  <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                    Group conversation
                  </p>
                </button>
              )}

              <button
                onClick={handleStartSupportChat}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700/50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-amber-600 dark:text-amber-300">
                  <HeadsetIcon />
                </div>
                <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>
                  Contact Support
                </p>
                <p className={`text-xs ${
                  supportAvailable ? "text-green-600 dark:text-green-400" : getMutedText(isDarkMode)
                }`}>
                  {supportAvailable ? "Agents online" : "Leave a message"}
                </p>
              </button>

              <button
                onClick={() => {
                  setShowArchived(true);
                  setView("list");
                }}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-colors ${
                  isDarkMode
                    ? "border-gray-700 hover:bg-gray-700/50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300">
                  <ArchiveIcon />
                </div>
                <p className={`font-medium text-sm ${getPrimaryText(isDarkMode)}`}>
                  Archived Chats
                </p>
                <p className={`text-xs ${getMutedText(isDarkMode)}`}>
                  Past conversations
                </p>
              </button>
            </div>
          ) : view === "list" ? (
            // Conversation List View
            <div className="p-2">
              {/* Active/Archived Toggle */}
              <div className="flex mb-2 p-1 rounded-lg bg-gray-100 dark:bg-gray-700">
                <button
                  onClick={() => setShowArchived(false)}
                  className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors ${
                    !showArchived
                      ? "bg-white dark:bg-gray-600 shadow-sm " + getPrimaryText(isDarkMode)
                      : getMutedText(isDarkMode) + " hover:bg-gray-50 dark:hover:bg-gray-600/50"
                  }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setShowArchived(true)}
                  className={`flex-1 py-1.5 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${
                    showArchived
                      ? "bg-white dark:bg-gray-600 shadow-sm " + getPrimaryText(isDarkMode)
                      : getMutedText(isDarkMode) + " hover:bg-gray-50 dark:hover:bg-gray-600/50"
                  }`}
                >
                  <ArchiveIcon />
                  Archived
                </button>
              </div>

              {/* Start Support Chat Button - only show when viewing active conversations */}
              {supportAvailable && !showArchived && (
                <button
                  onClick={handleStartSupportChat}
                  disabled={isLoading}
                  className={`w-full mb-2 p-3 rounded-lg flex items-center gap-3 transition-colors ${
                    isDarkMode
                      ? "bg-blue-900/30 hover:bg-blue-900/50 text-blue-300"
                      : "bg-blue-50 hover:bg-blue-100 text-blue-700"
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                    <HeadsetIcon />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">Contact Support</p>
                    <p className={`text-sm ${getMutedText(isDarkMode)}`}>
                      Support agents are online
                    </p>
                  </div>
                </button>
              )}

              {/* Loading State */}
              {isLoading && (showArchived ? archivedConversations : conversations).length === 0 && (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              {/* Empty State */}
              {!isLoading && (showArchived ? archivedConversations : conversations).length === 0 && (
                <div className={`text-center py-8 ${getMutedText(isDarkMode)}`}>
                  {showArchived ? (
                    <>
                      <div className="flex justify-center mb-2">
                        <ArchiveIcon />
                      </div>
                      <p className="mt-2">No archived chats</p>
                      <p className="text-sm">
                        Archived chats will appear here
                      </p>
                    </>
                  ) : (
                    <>
                      <UserIcon />
                      <p className="mt-2">No conversations yet</p>
                      <p className="text-sm">
                        Start a chat from a member's profile
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Conversation List */}
              {(showArchived ? archivedConversations : conversations).map((conv) => {
                const hasUnread = (conv.unread_count || 0) > 0;
                return (
                  <div
                    key={conv.id}
                    className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors relative group ${
                      isDarkMode
                        ? hasUnread
                          ? "bg-gray-700/50 hover:bg-gray-700"
                          : "hover:bg-gray-700"
                        : hasUnread
                          ? "bg-blue-50 hover:bg-blue-100"
                          : "hover:bg-gray-100"
                    }`}
                  >
                    {/* Unread indicator dot */}
                    {hasUnread && !showArchived && (
                      <div
                        className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-gray-700 dark:bg-gray-300"
                      />
                    )}
                    <button
                      onClick={() => handleConversationClick(conv)}
                      className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    >
                      {getConversationAvatar(conv)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p
                            className={`truncate ${
                              hasUnread && !showArchived ? "font-bold" : "font-medium"
                            } ${getPrimaryText(isDarkMode)}`}
                          >
                            {getConversationDisplayName(conv)}
                          </p>
                          {conv.last_message && (
                            <span
                              className={`text-xs ${
                                hasUnread && !showArchived
                                  ? isDarkMode
                                    ? "text-blue-400 font-medium"
                                    : "text-blue-600 font-medium"
                                  : getMutedText(isDarkMode)
                              }`}
                            >
                              {formatTimestamp(conv.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <p
                            className={`text-sm truncate ${
                              hasUnread && !showArchived
                                ? isDarkMode
                                  ? "text-gray-200 font-medium"
                                  : "text-gray-800 font-medium"
                                : getMutedText(isDarkMode)
                            }`}
                          >
                            {conv.last_message?.content || "No messages yet"}
                          </p>
                          {hasUnread && !showArchived && (
                            <span
                              className="min-w-[20px] h-5 px-1.5 rounded-full text-xs flex items-center justify-center font-bold bg-gray-700 text-white dark:bg-gray-300 dark:text-gray-900"
                            >
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {/* Archive/Unarchive Button */}
                    <button
                      aria-label={showArchived ? "Unarchive conversation" : "Archive conversation"}
                      onClick={(e) => {
                        if (showArchived) {
                          handleUnarchiveConversation(e, conv.id);
                        } else {
                          handleArchiveConversation(e, conv.id);
                        }
                      }}
                      className={`p-1.5 rounded-md transition-all ${
                        isDarkMode
                          ? "hover:bg-gray-600 text-gray-400 hover:text-gray-200"
                          : "hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                      }`}
                      title={showArchived ? "Unarchive chat" : "Archive chat"}
                    >
                      {showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : view === "categorySelector" ? (
            // Category Selector View
            <div className="p-4 flex flex-col h-full">
              {/* Header info */}
              <div className={`mb-4 p-3 rounded-lg ${
                isDarkMode ? "bg-gray-700/50" : "bg-gray-50"
              }`}>
                <p className={`text-sm ${getMutedText(isDarkMode)}`}>
                  What type of support do you need? Select a category to help us route your request to the right team.
                </p>
              </div>

              {/* Category buttons */}
              <div className="grid grid-cols-2 gap-3">
                {/* Billing */}
                <button
                  onClick={() => handleCategorySelect("billing")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-green-500 hover:bg-green-900/20"
                      : "bg-white border-gray-200 hover:border-green-500 hover:bg-green-50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Billing</span>
                  </div>
                </button>

                {/* Technical */}
                <button
                  onClick={() => handleCategorySelect("technical")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-blue-500 hover:bg-blue-900/20"
                      : "bg-white border-gray-200 hover:border-blue-500 hover:bg-blue-50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Technical</span>
                  </div>
                </button>

                {/* Account */}
                <button
                  onClick={() => handleCategorySelect("account")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-purple-500 hover:bg-purple-900/20"
                      : "bg-white border-gray-200 hover:border-purple-500 hover:bg-purple-50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Account</span>
                  </div>
                </button>

                {/* Feature Request */}
                <button
                  onClick={() => handleCategorySelect("feature")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-amber-500 hover:bg-amber-900/20"
                      : "bg-white border-gray-200 hover:border-amber-500 hover:bg-amber-50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Feature</span>
                  </div>
                </button>

                {/* Bug Report */}
                <button
                  onClick={() => handleCategorySelect("bug")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-red-500 hover:bg-red-900/20"
                      : "bg-white border-gray-200 hover:border-red-500 hover:bg-red-50"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Bug Report</span>
                  </div>
                </button>

                {/* Other */}
                <button
                  onClick={() => handleCategorySelect("other")}
                  className={`p-4 rounded-lg border transition-all hover:scale-[1.02] ${
                    isDarkMode
                      ? "bg-gray-700/50 border-gray-600 hover:border-gray-400 hover:bg-gray-600/50"
                      : "bg-white border-gray-200 hover:border-gray-400 hover:bg-gray-100"
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gray-500/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>Other</span>
                  </div>
                </button>
              </div>

              {/* Agent availability indicator */}
              <div className={`mt-4 p-3 rounded-lg text-center ${
                supportAvailable
                  ? isDarkMode ? "bg-green-900/20 text-green-300" : "bg-green-50 text-green-600"
                  : isDarkMode ? "bg-amber-900/20 text-amber-300" : "bg-amber-50 text-amber-600"
              }`}>
                <p className="text-sm">
                  {supportAvailable
                    ? "Support agents are currently online"
                    : "No agents currently available - you can leave a message"}
                </p>
              </div>
            </div>
          ) : view === "staffAi" ? (
            // Staff AI Assistant — embedded so we have a single floating
            // button. The dedicated panel was removed from SideNavbarLayout
            // in this commit; this view is the only entry point.
            <StaffAIAssistantPanel
              resetCounter={staffAiResetCounter}
              initialPrompt={assistantPrompt || undefined}
              promptNonce={assistantPromptNonce}
              onPromptConsumed={clearAssistantPrompt}
            />
          ) : view === "aiConversation" ? (
            // AI Conversation View
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {aiMessages.map((msg, idx) => {
                  if (msg.role === 'system') {
                    return (
                      <div key={idx} className="flex justify-center my-2">
                        <div className={`text-center px-4 py-2 rounded-full text-sm ${
                          isDarkMode ? "bg-gray-700/50 text-gray-300" : "bg-gray-100 text-gray-600"
                        }`}>
                          {msg.content}
                        </div>
                      </div>
                    );
                  }

                  if (msg.role === 'assistant') {
                    return (
                      <div key={idx} className="flex justify-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                        <div>
                          <p className={`text-xs mb-1 ${getMutedText(isDarkMode)}`}>AI Assistant</p>
                          <div className={`max-w-[85%] rounded-lg px-3 py-2 rounded-bl-sm ${
                            isDarkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-900"
                          }`}>
                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                            <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
                              {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // User message
                  return (
                    <div key={idx} className="flex justify-end">
                      <div
                        className="max-w-[75%] rounded-lg px-3 py-2 rounded-br-sm bg-gray-700 text-white dark:bg-gray-600"
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        <p className="text-xs mt-1 text-white/70">
                          {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* Typing indicator */}
                {isAiResponding && (
                  <div className="flex justify-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <div className={`rounded-lg px-3 py-2 ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}

                <div ref={aiMessagesEndRef} />
              </div>
            </div>
          ) : view === "newMessage" ? (
            // New Message View - Search for users
            <div className="p-3">
              {/* Search Input */}
              <div className="relative mb-3">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <SearchIcon />
                </div>
                <input aria-label="Search for people to message"
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search by name or email..."
                  className={`w-full pl-10 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
                  autoComplete="off"
                />
              </div>

              {/* Search Results */}
              {isSearching && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}

              {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className={`text-center py-4 ${getMutedText(isDarkMode)}`}>
                  <p>No users found</p>
                </div>
              )}

              {!isSearching && searchQuery.length < 2 && (
                <div className={`text-center py-4 ${getMutedText(isDarkMode)}`}>
                  <p>Type at least 2 characters to search</p>
                </div>
              )}

              {searchResults.map((resultUser) => (
                <button
                  key={resultUser.id}
                  onClick={() => handleSelectUser(resultUser)}
                  className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                  }`}
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                  >
                    {`${resultUser.first_name?.[0] || ""}${resultUser.last_name?.[0] || ""}`.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className={`font-medium truncate ${getPrimaryText(isDarkMode)}`}>
                      {`${resultUser.first_name} ${resultUser.last_name}`.trim()}
                    </p>
                    <p className={`text-sm truncate ${getMutedText(isDarkMode)}`}>
                      {resultUser.email}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          ) : view === "newChannel" ? (
            // New Channel View
            <div className="p-3 flex flex-col h-full">
              {/* Channel Name Input */}
              <div className="mb-4">
                <label htmlFor="channel-name" className={`block text-sm font-medium mb-1 ${getPrimaryText(isDarkMode)}`}>
                  Channel Name
                </label>
                <input id="channel-name"
                  ref={channelNameInputRef}
                  type="text"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="Enter channel name..."
                  className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
                  autoComplete="off"
                />
              </div>

              {/* Selected Users */}
              {selectedUsers.length > 0 && (
                <div className="mb-3">
                  <label className={`block text-sm font-medium mb-2 ${getPrimaryText(isDarkMode)}`}>
                    Members ({selectedUsers.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map((selectedUser) => (
                      <div
                        key={selectedUser.id}
                        className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                          isDarkMode ? "bg-gray-700" : "bg-gray-200"
                        }`}
                      >
                        <span className={getPrimaryText(isDarkMode)}>
                          {`${selectedUser.first_name} ${selectedUser.last_name}`.trim()}
                        </span>
                        <button aria-label="Remove from channel"
                          onClick={() => handleRemoveUserFromChannel(selectedUser.id)}
                          className={`ml-1 hover:text-red-500 ${getMutedText(isDarkMode)}`}
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Members Search */}
              <div className="flex-1 overflow-y-auto">
                <label className={`block text-sm font-medium mb-1 ${getPrimaryText(isDarkMode)}`}>
                  Add Members
                </label>
                <div className="relative mb-3">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <SearchIcon />
                  </div>
                  <input aria-label="Search for people to add to this channel"
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    placeholder="Search by name or email..."
                    className={`w-full pl-10 pr-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
                    autoComplete="off"
                  />
                </div>

                {/* Search Results */}
                {isSearching && (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {!isSearching && searchQuery.length >= 2 && searchResults.length === 0 && (
                  <div className={`text-center py-4 ${getMutedText(isDarkMode)}`}>
                    <p>No users found</p>
                  </div>
                )}

                {!isSearching && searchQuery.length < 2 && selectedUsers.length === 0 && (
                  <div className={`text-center py-4 ${getMutedText(isDarkMode)}`}>
                    <p>Search and add members to the channel</p>
                  </div>
                )}

                {searchResults
                  .filter(u => !selectedUsers.find(s => s.id === u.id))
                  .map((resultUser) => (
                    <button
                      key={resultUser.id}
                      onClick={() => handleAddUserToChannel(resultUser)}
                      className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                        isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                      }`}
                    >
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                      >
                        {`${resultUser.first_name?.[0] || ""}${resultUser.last_name?.[0] || ""}`.toUpperCase() || "?"}
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className={`font-medium truncate ${getPrimaryText(isDarkMode)}`}>
                          {`${resultUser.first_name} ${resultUser.last_name}`.trim()}
                        </p>
                        <p className={`text-sm truncate ${getMutedText(isDarkMode)}`}>
                          {resultUser.email}
                        </p>
                      </div>
                      <div className="text-blue-500">
                        <PlusIcon />
                      </div>
                    </button>
                  ))}
              </div>

              {/* Create Button */}
              <div className="pt-3 border-t mt-auto" style={{ borderColor: isDarkMode ? "#374151" : "#e5e7eb" }}>
                <button
                  onClick={handleCreateChannel}
                  disabled={!channelName.trim() || selectedUsers.length === 0 || isCreatingChannel}
                  className={`w-full py-2 rounded-lg font-medium transition-colors bg-[var(--cc-action)] text-[var(--cc-action-text)] ${
                    channelName.trim() && selectedUsers.length > 0 && !isCreatingChannel
                      ? "hover:bg-[var(--cc-action-hover)]"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                >
                  {isCreatingChannel ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </span>
                  ) : (
                    `Create Channel${selectedUsers.length > 0 ? ` (${selectedUsers.length} members)` : ""}`
                  )}
                </button>
              </div>
            </div>
          ) : view === "supportTicketForm" ? (
            // Support Ticket Form View
            <div className="p-4 flex flex-col h-full overflow-y-auto">
              {/* No agents message */}
              <div className={`mb-4 p-3 rounded-lg ${
                isDarkMode ? "bg-amber-900/30 border border-amber-700/50" : "bg-amber-50 border border-amber-200"
              }`}>
                <p className={`text-sm ${isDarkMode ? "text-amber-200" : "text-amber-700"}`}>
                  No support agents are currently online. Please submit a support ticket and we'll get back to you as soon as possible.
                </p>
              </div>

              {/* Subject */}
              <div className="mb-3">
                <Input
                  label="Subject *"
                  type="text"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                />
              </div>

              {/* Category */}
              <div className="mb-3">
                <label className={`block text-sm font-medium mb-1 ${getPrimaryText(isDarkMode)}`}>
                  Category
                </label>
                <select
                  value={ticketCategory}
                  onChange={(e) => setTicketCategory(e.target.value)}
                  className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
                >
                  <option value="general">General Inquiry</option>
                  <option value="technical">Technical Issue</option>
                  <option value="billing">Billing</option>
                  <option value="feature">Feature Request</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div className="mb-4 flex-1">
                <Textarea
                  label="Message *"
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows={5}
                  className="resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitTicket}
                disabled={!ticketSubject.trim() || !ticketMessage.trim() || isSubmittingTicket}
                className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                  ticketSubject.trim() && ticketMessage.trim() && !isSubmittingTicket
                    ? "hover:bg-[var(--cc-action-hover)]"
                    : "opacity-50 cursor-not-allowed"
                } bg-[var(--cc-action)] text-[var(--cc-action-text)]`}
              >
                {isSubmittingTicket ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </span>
                ) : (
                  "Submit Ticket"
                )}
              </button>
            </div>
          ) : view === "ticketSuccess" ? (
            // Ticket Success View
            <div className="p-6 flex flex-col items-center justify-center h-full text-center">
              <div className="text-green-500 mb-4">
                <CheckCircleIcon />
              </div>
              <h4 className={`text-lg font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>
                Ticket Submitted!
              </h4>
              <p className={`text-sm mb-6 ${getMutedText(isDarkMode)}`}>
                Your support ticket has been created. Our team will review it and get back to you soon.
              </p>
              <div className="space-y-3 w-full">
                <button
                  onClick={handleGoToTickets}
                  className={`w-full py-2.5 rounded-lg font-medium transition-colors bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]`}
                >
                  View My Tickets
                </button>
                <button
                  onClick={() => setView("list")}
                  className={`w-full py-2.5 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Back to Messages
                </button>
              </div>
            </div>
          ) : (
            // Conversation View
            <div className="flex flex-col h-full">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {isLoading && messages.length === 0 && (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}

                {messages.map((msg) => {
                  // Use getCurrentUserInfo() instead of user?.id because tenant_owner
                  // auth stores user info in localStorage, not in the Supabase session
                  const isOwn = msg.sender_id === getCurrentUserInfo()?.id;
                  const isSystem = msg.sender_type === "system";

                  // System messages (connecting, agent joined, timeout)
                  if (isSystem) {
                    return (
                      <div key={msg.id} className="flex justify-center my-2">
                        <div
                          className={`text-center px-4 py-2 rounded-full text-sm ${
                            isDarkMode
                              ? "bg-gray-700/50 text-gray-300"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {msg.content}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 ${
                          isOwn
                            ? "bg-gray-700 text-white dark:bg-gray-600 rounded-br-sm"
                            : isDarkMode
                              ? "bg-gray-700 text-white rounded-bl-sm"
                              : "bg-gray-100 text-gray-900 rounded-bl-sm"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {msg.content}
                        </p>
                        <p
                          className={`text-xs mt-1 ${
                            isOwn
                              ? "text-white/70"
                              : getMutedText(isDarkMode)
                          }`}
                        >
                          {formatTimestamp(msg.created_at)}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* Typing Indicator */}
                {typingUsers.length > 0 && (
                  <div className="flex justify-start">
                    <div
                      className={`rounded-lg px-3 py-2 ${
                        isDarkMode ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <div className="flex gap-1">
                        <span
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "0ms" }}
                        />
                        <span
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "150ms" }}
                        />
                        <span
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: "300ms" }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Waiting for Agent Indicator */}
                {isWaitingForAgent && activeConversation?.type === "support" && (
                  <div className="flex justify-center my-3">
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-50 text-blue-600"
                    }`}>
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">Waiting for agent...</span>
                    </div>
                  </div>
                )}

                {/* In-Chat Ticket Form (when no agent responds within 30 seconds) */}
                {showInChatTicketForm && activeConversation?.type === "support" && (
                  <div className={`mx-2 my-4 p-4 rounded-lg border ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200 shadow-sm"
                  }`}>
                    <h4 className={`font-medium mb-3 ${getPrimaryText(isDarkMode)}`}>
                      Submit a Support Ticket
                    </h4>

                    {/* Subject */}
                    <div className="mb-3">
                      <Input
                        label="Subject *"
                        type="text"
                        value={inChatTicketSubject}
                        onChange={(e) => setInChatTicketSubject(e.target.value)}
                        placeholder="Brief description of your issue"
                      />
                    </div>

                    {/* Category */}
                    <div className="mb-3">
                      <label className={`block text-xs font-medium mb-1 ${getMutedText(isDarkMode)}`}>
                        Category
                      </label>
                      <select
                        value={inChatTicketCategory}
                        onChange={(e) => setInChatTicketCategory(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
                      >
                        <option value="technical">Technical Issue</option>
                        <option value="billing">Billing & Payments</option>
                        <option value="account">Account Management</option>
                        <option value="feature">Feature Request</option>
                        <option value="bug">Bug Report</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div className="mb-3">
                      <Textarea
                        label="Message *"
                        value={inChatTicketMessage}
                        onChange={(e) => setInChatTicketMessage(e.target.value)}
                        placeholder="Please describe your issue in detail..."
                        rows={3}
                        className="resize-none"
                      />
                    </div>

                    {/* Error Message */}
                    {ticketSubmitError && (
                      <div className={`mb-3 p-2 rounded text-sm ${
                        isDarkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-600"
                      }`}>
                        {ticketSubmitError}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      onClick={handleSubmitInChatTicket}
                      disabled={!inChatTicketSubject.trim() || !inChatTicketMessage.trim() || isSubmittingInChatTicket}
                      className={`w-full py-2 rounded-lg font-medium text-sm transition-colors bg-[var(--cc-action)] text-[var(--cc-action-text)] ${
                        inChatTicketSubject.trim() && inChatTicketMessage.trim() && !isSubmittingInChatTicket
                          ? "hover:bg-[var(--cc-action-hover)]"
                          : "opacity-50 cursor-not-allowed"
                      }`}
                    >
                      {isSubmittingInChatTicket ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Submitting...
                        </span>
                      ) : (
                        "Submit Ticket"
                      )}
                    </button>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>
          )}
        </div>

        {/* Message Input (only in conversation view) */}
        {view === "conversation" && (
          <form
            onSubmit={handleSendMessage}
            className={`p-3 border-t ${getLightBorder(isDarkMode)}`}
          >
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={messageInput}
                onChange={handleInputChange}
                placeholder="Type a message..."
                className={`flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
              />
              <button aria-label="Send message"
                type="submit"
                disabled={!messageInput.trim()}
                className={`p-2 rounded-lg transition-colors ${
                  messageInput.trim()
                    ? "bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
                    : isDarkMode
                      ? "bg-gray-700 text-gray-500"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                <SendIcon />
              </button>
            </div>
          </form>
        )}

        {/* AI Chat Input */}
        {view === "aiConversation" && (
          <form
            onSubmit={handleSendAiMessage}
            className={`p-3 border-t ${getLightBorder(isDarkMode)}`}
          >
            <div className="flex items-center gap-2">
              <Tooltip content="Talk to a person"><button aria-label="Talk to a person"
                type="button"
                onClick={handleEscalateToHuman}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "hover:bg-gray-700 text-gray-400 hover:text-gray-200"
                    : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                }`}

              >
                <HeadsetIcon />
              </button></Tooltip>
              <input aria-label="Message"
                type="text"
                value={messageInput}
                onChange={(e) => setMessageInput(e.target.value)}
                placeholder="Type a message..."
                disabled={isAiResponding}
                className={`flex-1 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${getInputStyles(isDarkMode)}`}
              />
              <button
                aria-label="Send message"
                type="submit"
                disabled={!messageInput.trim() || isAiResponding}
                className={`p-2 rounded-lg transition-colors ${
                  messageInput.trim() && !isAiResponding
                    ? "bg-[var(--cc-action)] text-[var(--cc-action-text)] hover:bg-[var(--cc-action-hover)]"
                    : isDarkMode
                      ? "bg-gray-700 text-gray-500"
                      : "bg-gray-100 text-gray-400"
                }`}
              >
                <SendIcon />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
